import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  ListGroup,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchDynamicGroupAllAgents,
  saveAgentsInGroupDispatcher,
} from "../../store/slices/smartConnect/dynamicAgentGroup";
import { fetchCreditqAgents } from "../../store/slices/smartConnect/creditqAgents";

function AddNewAgentsModal({ show, onClose, groupId }) {
  const dispatch = useDispatch();

  const { allAgents } = useSelector((store) => store.dynamicAgentGroupReducer);

  const { data: existingAgents = [], error } = allAgents || {};

  const [departmentAgents, setDepartmentAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [activeDepartment, setActiveDepartment] = useState("");
  const [search, setSearch] = useState("");
  const [deptLoading, setDeptLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const existingAgentIds = useMemo(
    () => new Set(existingAgents.map((a) => a.cq_agent_id)),
    [existingAgents],
  );

  const fetchExistingAgents = useCallback(() => {
    if (!groupId) return;

    dispatch(
      fetchDynamicGroupAllAgents({
        filters: {
          group_id: groupId,
          pagination: false,
        },
      }),
    );
  }, [dispatch, groupId]);

  useEffect(() => {
    if (show && groupId) {
      fetchExistingAgents();
    }
  }, [show, groupId, fetchExistingAgents]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const fetchDepartmentAgents = async (department) => {
    try {
      setDeptLoading(true);
      setActiveDepartment(department);
      setSearch("");

      const res = await dispatch(
        fetchCreditqAgents({
          agentType: department,
          status: "ACTIVE",
        }),
      ).unwrap();

      const agents = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];

      setDepartmentAgents(agents);
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Failed to load agents",
      );
    } finally {
      setDeptLoading(false);
    }
  };

  useEffect(() => {
    if (!departmentAgents.length) return;

    const preselected = departmentAgents.filter((a) =>
      existingAgentIds.has(a.cq_id),
    );

    setSelectedAgents((prev) => {
      const map = new Map(prev.map((a) => [a.cq_id, a]));
      preselected.forEach((a) => map.set(a.cq_id, a));
      return Array.from(map.values());
    });
  }, [departmentAgents, existingAgentIds]);

  const filteredAgents = useMemo(() => {
    if (!search) return departmentAgents;
    const q = search.toLowerCase();
    return departmentAgents.filter(
      (a) =>
        a.agent_name?.toLowerCase().includes(q) ||
        a.agent_email?.toLowerCase().includes(q),
    );
  }, [departmentAgents, search]);

  const toggleAgent = (agent) => {
    if (existingAgentIds.has(agent.cq_id)) return;

    setSelectedAgents((prev) =>
      prev.some((a) => a.cq_id === agent.cq_id)
        ? prev.filter((a) => a.cq_id !== agent.cq_id)
        : [...prev, agent],
    );
  };

  const removeAgent = (cqId) => {
    if (existingAgentIds.has(cqId)) return;
    setSelectedAgents((prev) => prev.filter((a) => a.cq_id !== cqId));
  };

  const handleSave = async () => {
    const newAgents = selectedAgents.filter(
      (a) => !existingAgentIds.has(a.cq_id),
    );

    if (!newAgents.length) {
      toast.info("No new agents selected");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.data?.id) {
      toast.error("User not found. Please re-login.");
      return;
    }

    try {
      setSaving(true);

      const msg = await dispatch(
        saveAgentsInGroupDispatcher({
          agents: newAgents,
          user_id: user.data.id,
          group_id: groupId,
        }),
      ).unwrap();

      toast.success(msg || "Agents added successfully");
      onClose();
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Failed to add agents",
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!show) {
      setDepartmentAgents([]);
      setSelectedAgents([]);
      setActiveDepartment("");
      setSearch("");
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Agents to Group</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <Button
            size="sm"
            variant="outline-primary"
            disabled={deptLoading}
            onClick={() => fetchDepartmentAgents("CSM")}
          >
            Load CSM
          </Button>

          <Button
            size="sm"
            variant="outline-success"
            disabled={deptLoading}
            onClick={() => fetchDepartmentAgents("SALES_SETTLEMENT")}
          >
            Load Sales / Settlement
          </Button>

          <Button
            size="sm"
            variant="outline-warning"
            disabled={deptLoading}
            onClick={() => fetchDepartmentAgents("SALES")}
          >
            Load Sales
          </Button>
        </div>

        {deptLoading && (
          <div className="text-center my-2">
            <Spinner size="sm" />
          </div>
        )}

        {departmentAgents.length > 0 && (
          <>
            <InputGroup className="mb-2">
              <Form.Control
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <ListGroup style={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredAgents.map((agent) => {
                const isExisting = existingAgentIds.has(agent.cq_id);

                return (
                  <ListGroup.Item key={agent.cq_id}>
                    <Form.Check
                      type="checkbox"
                      disabled={isExisting}
                      checked={
                        isExisting ||
                        selectedAgents.some((a) => a.cq_id === agent.cq_id)
                      }
                      onChange={() => toggleAgent(agent)}
                      label={
                        <>
                          <strong>{agent.agent_name}</strong>
                          <div className="text-muted small">
                            {agent.agent_email} • {agent.departments}
                            {isExisting && (
                              <span className="ms-2 text-success">
                                (Already in group)
                              </span>
                            )}
                          </div>
                        </>
                      }
                    />
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </>
        )}

        {selectedAgents.length > 0 && (
          <div className="mt-2 d-flex flex-wrap gap-2">
            {selectedAgents.map((a) =>
              existingAgentIds.has(a.cq_id) ? null : (
                <Badge
                  key={a.cq_id}
                  bg="secondary"
                  pill
                  style={{ cursor: "pointer" }}
                  onClick={() => removeAgent(a.cq_id)}
                >
                  {a.agent_name} ✕
                </Badge>
              ),
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={saving} onClick={handleSave}>
          {saving ? "Saving..." : "Add Agents"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddNewAgentsModal;
